import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const SAUDI_TIMEZONE = "Asia/Riyadh";
const RAMADAN_MONTH = 9;
const SHAWWAL_MONTH = 10;
const MAY = 4;
const HIJRI_PARTS_FORMATTER = new Intl.DateTimeFormat(
  "en-u-ca-islamic-umalqura",
  {
    timeZone: SAUDI_TIMEZONE,
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }
);

export const toSaudiDay = dateLike => {
  if (!dateLike) {
    return null;
  }

  const parsedDate = dayjs.isDayjs(dateLike) ? dateLike : dayjs(dateLike);

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.tz(SAUDI_TIMEZONE);
};

export const getSaudiHijriParts = dateLike => {
  const saudiDate = toSaudiDay(dateLike);

  if (!saudiDate) {
    return null;
  }

  const parts = HIJRI_PARTS_FORMATTER.formatToParts(saudiDate.toDate());
  const month = Number(parts.find(part => part.type === "month")?.value);
  const day = Number(parts.find(part => part.type === "day")?.value);

  if (!month || !day) {
    return null;
  }

  return { month, day };
};

export const isMonthEndBlocked = dateLike => {
  const saudiDate = toSaudiDay(dateLike);

  if (!saudiDate) {
    return false;
  }

  return saudiDate.date() > saudiDate.daysInMonth() - 2;
};

export const isMay2026ClosureBlocked = dateLike => {
  const saudiDate = toSaudiDay(dateLike);

  if (!saudiDate) {
    return false;
  }

  return (
    saudiDate.year() === 2026 &&
    saudiDate.month() === MAY &&
    saudiDate.date() >= 26 &&
    saudiDate.date() <= 31
  );
};

export const isRamadanOrEidBlocked = dateLike => {
  const hijriParts = getSaudiHijriParts(dateLike);

  if (!hijriParts) {
    return false;
  }

  const { month, day } = hijriParts;

  if (month === RAMADAN_MONTH) {
    return day >= 21 && day <= 30;
  }

  if (month === SHAWWAL_MONTH) {
    return day >= 1 && day <= 4;
  }

  return false;
};

export const isBlockedBookingDate = dateLike =>
  isMonthEndBlocked(dateLike) ||
  isMay2026ClosureBlocked(dateLike) ||
  isRamadanOrEidBlocked(dateLike);

export const getNextValidBookingDate = (
  startDate,
  { maxDate = null, maxTries = 60 } = {}
) => {
  let candidate = toSaudiDay(startDate);
  const maxCandidate = toSaudiDay(maxDate);

  if (!candidate) {
    return null;
  }

  for (let i = 0; i < maxTries; i += 1) {
    if (maxCandidate && candidate.isAfter(maxCandidate, "day")) {
      return null;
    }

    if (!isBlockedBookingDate(candidate)) {
      return candidate;
    }

    candidate = candidate.add(1, "day");
  }

  return null;
};
